import { useCallback, useMemo, useState } from 'react';
import type { ColumnFiltersState, OnChangeFn, SortingState } from '@tanstack/react-table';
import type { OrderBy } from '@/lib/graphql-query-builder';
import { buildColumnFiltersWhere, buildSortingOrder } from '@/lib/table-query';
import { useIsMobile } from './use-media-query';

/** Rows per page a table starts with on a phone (below 768px), whatever its desktop size. */
export const MOBILE_PAGE_SIZE = 10;

/**
 * The page size a table starts with: `desktopPageSize`, or `MOBILE_PAGE_SIZE` on a phone. Read once
 * for the initial state; a later pick from the page-size menu is kept.
 */
export const useInitialPageSize = (desktopPageSize: number) =>
  useIsMobile() ? MOBILE_PAGE_SIZE : desktopPageSize;

interface UseServerTableOptions {
  /** Rows per page (default 10); a phone always starts at `MOBILE_PAGE_SIZE`. */
  pageSize?: number;
  /** The list's own order, used while no column is sorted. */
  defaultOrder?: OrderBy[];
  /** A record the page was linked to (useLinkedRecord): while set, only it is listed, on page one. */
  linkedId?: string;
}

/**
 * Paging, sorting and column filters of an `AdvanceDataTable` whose rows come from the Data Gateway
 * page by page. Sorting and filtering run on the server (`order`, `columnWhere`), never on the current
 * page only, and both go back to the first page. Merge `columnWhere` into the list's own filter with
 * `mergeWhere`, and spread `tableProps` onto the table:
 *
 * ```tsx
 * const table = useServerTable({ defaultOrder: [{ field: 'Title', direction: 'ASC' }] });
 * const { data } = useGetDepartments({
 *   pageNo: table.pagination.pageIndex + 1,
 *   pageSize: table.pagination.pageSize,
 *   filter: mergeWhere(filter, table.columnWhere),
 *   order: table.order,
 * });
 * <AdvanceDataTable {...table.tableProps} pagination={{ ...table.pagination, totalCount }} ... />
 * ```
 */
export const useServerTable = ({
  pageSize = 10,
  defaultOrder,
  linkedId,
}: UseServerTableOptions = {}) => {
  const initialPageSize = useInitialPageSize(pageSize);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: initialPageSize });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, pageIndex: 0 })), []);

  const onPaginationChange = useCallback(
    (next: { pageIndex: number; pageSize: number }) => setPagination(next),
    []
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSorting(updater);
      resetPage();
    },
    [resetPage]
  );

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      setColumnFilters(updater);
      resetPage();
    },
    [resetPage]
  );

  const defaultOrderKey = JSON.stringify(defaultOrder ?? []);
  const order = useMemo(
    () => buildSortingOrder(sorting, JSON.parse(defaultOrderKey) as OrderBy[]),
    [sorting, defaultOrderKey]
  );
  const columnWhere = useMemo(() => {
    const where = buildColumnFiltersWhere(columnFilters);
    return linkedId ? { ...where, ItemId: linkedId } : where;
  }, [columnFilters, linkedId]);
  // The page picked before is kept for when the whole list shows again.
  const shownPagination = linkedId ? { ...pagination, pageIndex: 0 } : pagination;

  return {
    pagination: shownPagination,
    setPagination,
    resetPage,
    order,
    columnWhere,
    tableProps: {
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      sorting,
      onSortingChange,
      columnFilters,
      onColumnFiltersChange,
      onPaginationChange,
    },
  };
};
